import { forwardRef, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Icon, Paragraph } from '@ets/design-system';

import MathML from './MathML';

import { MMLHelper } from '_common/utils/MMLHelper';
import { Structure, Symbol } from './ToolbarElements';

type EquationElement = (Structure['elements'][number] | Symbol['elements'][number]) & {
  _equation?: string;
  tex?: string;
};

type PreviewProps = {
  value: string;
  isValid?: boolean;
  equation?: EquationElement;
};

const Preview = forwardRef<HTMLDivElement, PreviewProps>(({ value, isValid, equation }, ref) => {
  const [mml, setMml] = useState<string | undefined>();

  useEffect(() => {
    if (!equation || value !== equation.tex) {
      setMml(MMLHelper.latexToMML(value));
    }

    if (equation && value === equation.tex) {
      setMml(equation.mathml ?? equation._equation);
    }
  }, [value, equation]);

  return (
    <div className="mb-16 text-neutral-100">
      <div className="text-2xl leading-8">
        <FormattedMessage id="PREVIEW" />:
      </div>
      <div className="flex h-76 items-center justify-center">
        {!value && (
          <Paragraph
            className="text-neutral-50"
            size="l"
            weight="500"
            testId="equation-preview-empty"
          >
            <FormattedMessage id="EQUATION_PREVIEW" />
          </Paragraph>
        )}
        {isValid && (
          <MathML mml={mml} tooltip={equation?.label} testId={'equation-preview-content'} />
        )}

        {value && !isValid && (
          <Paragraph
            className="flex items-center"
            size="l"
            weight="500"
            testId="invalid-equation-preview"
          >
            <Icon icon="triangle-exclamation" size="4" testId="warning" color="warning" />
            <FormattedMessage id="INVALID_EQUATION" />
          </Paragraph>
        )}
      </div>
    </div>
  );
});

export default Preview;
